/* eslint-disable react-hooks/rules-of-hooks */

import * as React from 'react';
import { StyledTab, StyledTabs, StyledTabPanel } from './Tabs.styles';

type TabProps = {
    label: string;
};
export const Tab: React.FC<TabProps> = () => {
    throw new Error(
        `The 'Tab' component is not meant to be rendered. It's an abstract component that is only valid as a direct child of the 'Tabs' component.`
    );
};

type TabsProps = {
    initialIndex?: number;
    ariaLabel: string;
    isFullWidth?: boolean;
    handleClick?: (index: number) => void;
};
export const Tabs: React.FC<TabsProps> = (props) => {
    const {
        initialIndex = 0,
        ariaLabel,
        isFullWidth = false,
        handleClick,
        children
    } = props;

    if (!children) return null;
    const [selectedTab, setSelectedTab] = React.useState(initialIndex);

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        const target = event.target as HTMLElement;
        const role = target && target.getAttribute('role');
        if (role !== 'tab') return;

        const focus = (newTarget: HTMLElement | null) => {
            if (newTarget !== null) {
                newTarget.focus();
                event.preventDefault();
            }
        };

        if (event.key === 'ArrowLeft') {
            focus(target.previousElementSibling as HTMLElement);
        }

        if (event.key === 'ArrowRight') {
            focus(target.nextElementSibling as HTMLElement);
        }
    };

    const changeTab = (index: number) => {
        setSelectedTab(index);
        handleClick && handleClick(index);
    };

    return (
        <>
            <StyledTabs
                onKeyDown={handleKeyDown}
                role="tablist"
                aria-label={ariaLabel}
                isFullWidth={isFullWidth}
                {...props}
            >
                {React.Children.map(children, (child, index) => {
                    if (!React.isValidElement(child)) return;
                    const isSelected = index === selectedTab;
                    return (
                        <StyledTab
                            key={`tab-${index}`}
                            id={`tab-${index}`}
                            role="tab"
                            type="button"
                            isSelected={isSelected}
                            aria-selected={isSelected}
                            aria-controls={`tabpanel-${index}`}
                            onClick={() => changeTab(index)}
                            onKeyDown={(event) =>
                                event.key === 'Enter' && changeTab(index)
                            }
                            tabIndex={isSelected ? 0 : -1}
                        >
                            {child.props.label}
                        </StyledTab>
                    );
                })}
            </StyledTabs>

            {React.Children.map(children, (child, index) => {
                if (!React.isValidElement(child)) return;
                const isSelected = index === selectedTab;
                return (
                    <StyledTabPanel
                        role="tabpanel"
                        hidden={!isSelected}
                        key={`tabpanel-${index}`}
                        id={`tabpanel-${index}`}
                        aria-labelledby={`tab-${index}`}
                    >
                        {selectedTab === index && child.props.children}
                    </StyledTabPanel>
                );
            })}
        </>
    );
};
